import Headline from '@allesauto/core/headline';
import NextImage from '@allesauto/core/nextImage';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import {
  Colors,
  FieldTypeAsset,
  FieldTypeHTML,
  FieldTypeLink,
  theme,
} from '@allesauto/utils/consts';
import { getUrlFromStoryblokLink } from '@allesauto/utils/funcs';
import { createMarkup } from '@allesauto/utils/funcs';
import { ArrowRight32 } from '@carbon/icons-react';
import { renderRichText } from '@storyblok/react';
import clsx from 'clsx';
import { useRouter } from 'next/router';

export enum ImageVariants {
  FULL_SURFACE = 'FULL_SURFACE',
  TRANSPARENT = 'TRANSPARENT',
}

export interface BoxPictureTextProps {
  _uid: string;
  headline: string;
  headlineTag: 3 | 4;
  headlineColor: Colors;
  headlineFontSize?: 'title' | 'headline2' | 'headline3' | 'headline4';
  text: FieldTypeHTML;
  textColor: Colors.WHITE | Colors.DARK_GREY;
  background: Colors;
  buttonColor: Colors;
  image?: FieldTypeAsset;
  imageVariant: ImageVariants;
  link?: FieldTypeLink;
  trackId: string;
  textFirst?: boolean;
  contentClassName?: string;
}

const cssClasses = {
  container: 'group flex flex-col',
  headline: 'mb-[10px]',
  button: clsx(
    'flex items-center justify-center max-h-[40px] max-w-[40px] min-h-[40px] min-w-[40px] rounded-circle self-end',
    'duration-300 ease-in-out transform group-hover:bg-red60 group-hover:text-white group-hover:translate-x-[10px]'
  ),
  content: 'flex p-30px md:p-[30px_35px_35px_50px] gap-[8px] h-full',
  text: 'transform ease-in-out duration-300 pb-30px',
  transparentImage: clsx(
    'flex h-[226px] items-end px-30px relative w-full md:pt-[36px] md:px-[50px]',
    'duration-300 ease-in-out transform group-hover:scale-110'
  ),
  fullSurfaceImage:
    'bg-center bg-cover bg-no-repeat duration-300 ease-in-out h-full transform w-full group-hover:scale-110',
  fullSurfaceImageContainer: 'w-full h-[226px] overflow-hidden',
};

const BoxImage = ({
  image,
  renderAs,
}: {
  image: FieldTypeAsset;
  renderAs: ImageVariants;
}) => {
  const { filename, alt = '' } = image;

  if (renderAs == ImageVariants.TRANSPARENT) {
    return (
      <NextImage
        className={cssClasses.transparentImage}
        layout="fill"
        src={filename}
        alt={alt}
        objectPosition="bottom"
        objectFit="contain"
        imgClassName="!static"
      />
    );
  }

  return (
    <div className={cssClasses.fullSurfaceImageContainer}>
      <div
        style={{ backgroundImage: `url('${filename}')` }}
        className={cssClasses.fullSurfaceImage}
      />
    </div>
  );
};

const BoxPictureText = (props: BoxPictureTextProps) => {
  const {
    headline,
    headlineColor,
    headlineFontSize,
    headlineTag,
    image,
    imageVariant,
    text,
    textColor,
    link,
    buttonColor,
    background,
    trackId,
    textFirst = false,
    contentClassName = '',
  } = props;
  const router = useRouter();

  const buttonLink = link ? getUrlFromStoryblokLink(link) : '';

  const onBoxClick = () => {
    pushAnalyticsEvent({
      event: trackId,
      link: buttonLink,
    });
    router.push(buttonLink);
  };

  return (
    <div className={clsx(theme[background].background, cssClasses.container)}>
      {image?.filename && (
        <div className={clsx(textFirst && 'order-2')}>
          <BoxImage image={image} renderAs={imageVariant} />
        </div>
      )}

      <div className={clsx(cssClasses.content, contentClassName)}>
        <div
          className={clsx(
            cssClasses.text,
            textFirst
              ? 'group-hover:translate-y-[-10px]'
              : 'group-hover:translate-y-[10px]'
          )}
        >
          <Headline
            headlineTag={headlineTag}
            headlineText={headline}
            headlineColor={theme[headlineColor].text}
            headlineClassName={clsx(
              cssClasses.headline,
              headlineFontSize && `text-${headlineFontSize}`
            )}
          />
          <div
            className={theme[textColor].text}
            dangerouslySetInnerHTML={createMarkup(
              text?.type ? renderRichText(text) : String(text)
            )}
          />
        </div>
        {buttonLink && (
          <button
            type="button"
            className={clsx(
              cssClasses.button,
              theme[buttonColor].background,
              theme[background].text
            )}
            onClick={onBoxClick}
          >
            <ArrowRight32 width={24} height={24} />
          </button>
        )}
      </div>
    </div>
  );
};

export default BoxPictureText;
